import { Typography } from '@material-ui/core';
import { FeatureFlag, Site } from '@ps/web-core-enums';
import { Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import { isEmpty, partition } from 'lodash';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { CSSTransition } from 'react-transition-group';

import CrmZone from '@swf/components/CrmZone/CrmZone';
import { Context as NavigationContext } from '@swf/components/StarsWizard/components/Navigation/Navigation';
import PlusIcon from '@swf/core/assets/icons/plus-icon.svg';
import { CRMZone } from '@swf/core/enums';
import useFeature from '@swf/core/hooks/useFeature';
import useIsMounted from '@swf/core/hooks/useIsMounted';
import { useNativeLoader } from '@swf/core/hooks/useNativeLoader';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import useWizardExit from '@swf/core/hooks/useWizardExit';
import useWizardJump from '@swf/core/hooks/useWizardJump';
import { select } from '@swf/core/store';
import { canUseDOM } from '@swf/core/utils';

import Prompt from '../../../../components/Prompt/Prompt';
import type { HelpSection } from '../../../../types/Cashier';
import { executeHistoryPush } from '../../../../utils/cashier';
import Alert from '../../../Alert/Alert';
import BonusSummary from '../../../BonusSummary/BonusSummary';
import Container from '../../../Container/Container';
import Footer from '../../../Container/components/Footer/Footer';
import ListItem from '../../../ListItem/ListItem';
import PaymentMethodSelect from '../../../PaymentMethodSelect/PaymentMethodSelect';
import SuggestedPayment from '../../../Prompts/SuggestedPayment/SuggestedPayment';
import TransitionProgressPrompt from '../../../TransitionProgressPrompt/TransitionProgressPrompt';
import { duration, ltr, rtl } from '../../../WizardGroup/components/Transition/Transition';
import HelpSectionContent from '../DepositMethod/components/HelpSectionContent/HelpSectionContent';

import {
  TXTCLI_Almost_done,
  TXTCLI_Once_you_depositX_youXll_be_go_ELL,
  TXTCLI_Other_payment_methods,
  TXTWCL_Add_new_payment_method,
  TXTWCL_Almost_there,
  TXTWCL_Bonus_ready_payment_methods,
  TXTWCL_Continue,
  TXTWCL_Continue_to_deposit,
  TXTWCL_Deposit,
  TXTWCL_Deposit_methods_help,
  TXTWCL_How_would_you_like_to_depositX,
  TXTWCL_How_would_you_like_to_depositX_ELL,
  TXTWCL_Make_your_first_deposit_and_cl_ELL,
  TXTWCL_Next_upXX,
  TXTWCL_Payment_methods,
  TXTWCL_Preferred_payment_method,
  TXTWCL_Sorry_your_saved_payment_metho_ELL,
  TXTWCL_Your_other_payment_method,
  TXTWCL_Your_other_payment_methods,
  TXTWCL_Your_payment_method,
  TXTWCL_Your_payment_methods
} from './DepositMethod.messages.json';
import {
  alert,
  alertHeading,
  container,
  crm,
  gridLayout,
  innerPage,
  listItem,
  plusIcon,
  transitionContainer,
  transitionInnerContainer,
  wrapper
} from './DepositMethod.scss';
import BonusIneligiblePrompt from './components/BonusIneligiblePrompt/BonusIneligiblePrompt';
import TempAccountMessage from './components/TempAccountMessage/TempAccountMessage';

const setNativeBonusVisibility = (visibility: boolean) =>
  window?.starsweb?.app.setDepositBonusVisibility?.(visibility);

const DepositMethod: React.FC = () => {
  const {
    values: { firstName: wizardFirstName, deposit: wizardDepositValues },
    data
  } = useWizardContext();
  const {
    savedCards = [],
    bonusReadySystems,
    crmBonusCode,
    paySystems,
    paySystemDetails = {},
    rmfPassed,
    signupPassed,
    cmsHeaderAndGridLayout,
    pendingCashouts
  } = data;
  const { bonusCode } = wizardDepositValues || {};
  const isReloader = useSelector(select.account.isReloader);
  const isEmbedded = useSelector(select.device.isEmbedded);
  const hideBonusCode = useSelector(select.account.hideBonusCode);
  const isRomanianTemporaryAccount = useSelector(select.account.isRomanianTemporaryAccount);
  const firstName = useSelector(select.account.getFirstName) || wizardFirstName;
  const isSWCWelcomeBonusScreenEnabled = useFeature(FeatureFlag.SwcWelcomeBonusScreen);
  const isSWCAffiliateBonusEnabled = useFeature(FeatureFlag.SwcAffiliateBonus);
  const isFrance = useSelector(select.cms.getSiteId) === Site.FR;
  const isSwcSignupRmfEcoEransitionTwo = useFeature(FeatureFlag.SwcSignupRmfEcoEransitionTwo);
  const exitWizard = useWizardExit();
  const { jumpBack } = useWizardJump();
  const allSavedCardsHostile = !!(
    savedCards.length && savedCards.every(({ savedCard }) => savedCard.hostileBin)
  );
  const preferredCardType = allSavedCardsHostile
    ? savedCards.find((card) => card.savedCard.fastDeposit === true)?.savedCard.paySystem
    : undefined;
  const [showSavedCards, setShowSavedCards] = useState(
    !!savedCards.length && !data.regularMethodSelected && !allSavedCardsHostile
  );
  const [skipSavedMethods, setSkipSavedMethods] = useState(
    !!savedCards.length && !!data.regularMethodSelected
  );
  const [showIneligiblePrompt, setShowIneligiblePrompt] = useState(false);
  const [showSuggestedPaymentPrompt, setShowSuggestedPaymentPrompt] =
    useState(allSavedCardsHostile);

  const [isTransitioning, setIsTransitioning] = useState(false);
  const { goPrevious } = useContext(NavigationContext);
  const [bonusIneligibleCallback, setBonusIneligibleCallback] = useState<() => void>();
  const isMounted = useIsMounted();
  const site = useSelector(select.cms.getSiteId);

  useNativeLoader();

  const showTempAccountMessage =
    isRomanianTemporaryAccount && (!savedCards.length || allSavedCardsHostile);
  const applyGridLayout = cmsHeaderAndGridLayout && !isEmbedded;
  const navigateBack =
    (!!isSWCWelcomeBonusScreenEnabled &&
      !!(isSWCAffiliateBonusEnabled || crmBonusCode) &&
      !signupPassed &&
      !rmfPassed &&
      !isReloader &&
      !isEmbedded &&
      !hideBonusCode) ||
    !isEmpty(pendingCashouts);

  const showExitPrompt = () => {
    if (isEmbedded) {
      return savedCards.length ? !showSavedCards : false;
    }
    return !(rmfPassed && isEmpty(wizardDepositValues));
  };
  const [otherSystems, bonusSystems] = partition(paySystems, (system) =>
    bonusReadySystems ? !bonusReadySystems.includes(system.cardType) : paySystems
  );
  const [otherSavedSystems, fastDepositSystem] = partition(
    savedCards,
    ({ savedCard }) => !savedCard.fastDeposit
  );

  const subtitle = (
    <Translation
      {...(firstName
        ? TXTWCL_How_would_you_like_to_depositX_ELL
        : TXTWCL_How_would_you_like_to_depositX)}
      values={{ 0: firstName }}
      tagName="h2"
    />
  );

  const goBack = useCallback(() => {
    if (skipSavedMethods) {
      setIsTransitioning(true);
      setShowSavedCards(true);
      setSkipSavedMethods(false);
      delete data.regularMethodSelected;
    } else {
      navigateBack
        ? !isEmpty(pendingCashouts)
          ? jumpBack('#deposit.deposit-pending-cashout-menu')
          : goPrevious()
        : exitWizard();
    }
  }, [
    skipSavedMethods,
    data.regularMethodSelected,
    navigateBack,
    pendingCashouts,
    jumpBack,
    goPrevious,
    exitWizard
  ]);

  const embeddedProp = (visibility: boolean) =>
    isEmbedded
      ? {
          setNativeBonusVisibility: () => setNativeBonusVisibility(visibility)
        }
      : {};

  const setTitle = () => {
    if (fastDepositSystem.length) {
      if (otherSavedSystems.length > 1) {
        return TXTWCL_Your_other_payment_methods;
      }
      return TXTWCL_Your_other_payment_method;
    }
    if (otherSavedSystems.length > 1) {
      return TXTWCL_Your_payment_methods;
    }
    return TXTWCL_Your_payment_method;
  };

  const CRMZoneContent = (
    <div className={crm}>
      <CrmZone zoneId={CRMZone.SWC_DEPOSIT_MAIN} componentType="Single" dynamicHeight visible />
    </div>
  );

  const regularMethodsScreen = (
    <>
      <div className={wrapper}>
        <BonusSummary />
        {showTempAccountMessage && <TempAccountMessage />}
        {subtitle}
        {CRMZoneContent}
        {allSavedCardsHostile && (
          <>
            {showSuggestedPaymentPrompt && preferredCardType && (
              <SuggestedPayment
                onClose={() => setShowSuggestedPaymentPrompt(false)}
                cardType={preferredCardType}
                paySystems={paySystems}
              />
            )}
            <Alert variant="warning" className={alert}>
              <>
                <div className={alertHeading}>
                  <Translation {...TXTWCL_Payment_methods} />
                </div>
                <div>
                  <Translation {...TXTWCL_Sorry_your_saved_payment_metho_ELL} />
                </div>
              </>
            </Alert>
          </>
        )}
        {!!bonusSystems.length && (
          <PaymentMethodSelect
            fieldName="deposit"
            heading={TXTWCL_Bonus_ready_payment_methods}
            options={bonusSystems}
            variant="bonusReady"
            disable
            {...embeddedProp(true)}
          />
        )}
        {!!otherSystems.length && (
          <PaymentMethodSelect
            fieldName="deposit"
            heading={bonusSystems.length ? TXTCLI_Other_payment_methods : TXTWCL_Payment_methods}
            options={otherSystems}
            disable={!bonusSystems.length}
            {...(bonusSystems.length && {
              onClick: (callback) => {
                setShowIneligiblePrompt(true);
                setBonusIneligibleCallback(() => callback);
              }
            })}
            {...embeddedProp(!!bonusCode && !bonusSystems.length)}
          />
        )}
      </div>
      {!cmsHeaderAndGridLayout && <Footer showFooter />}
    </>
  );

  const savedMethodsScreen = (
    <>
      <div className={wrapper}>
        <BonusSummary />
        {isRomanianTemporaryAccount && <TempAccountMessage />}
        {subtitle}
        {CRMZoneContent}
        {!!fastDepositSystem.length && (
          <PaymentMethodSelect
            fieldName="deposit"
            heading={TXTWCL_Preferred_payment_method}
            options={fastDepositSystem}
            disable
            isSavedCard
          />
        )}
        {!!otherSavedSystems.length && (
          <PaymentMethodSelect
            fieldName="deposit"
            heading={setTitle()}
            options={otherSavedSystems}
            disable
            isSavedCard
          />
        )}
        <ListItem
          primary={<Translation {...TXTWCL_Add_new_payment_method} />}
          avatar={<PlusIcon />}
          classes={{
            avatar: classNames(plusIcon, { [gridLayout]: applyGridLayout }),
            root: classNames(listItem, { [gridLayout]: applyGridLayout })
          }}
          onClick={() => {
            delete data.savedCardInfoId;
            setSkipSavedMethods(true);
            if (canUseDOM && applyGridLayout) {
              (document.documentElement as HTMLElement).scrollTop = 0;
            }
          }}
        />
      </div>
      {!cmsHeaderAndGridLayout && <Footer showFooter />}
    </>
  );

  useEffect(() => {
    if (skipSavedMethods && isMounted()) {
      setShowSavedCards(false);
      setIsTransitioning(true);
      isEmbedded && executeHistoryPush(isEmbedded);
    }
  }, [isEmbedded, skipSavedMethods, isMounted]);

  const helpSection: HelpSection = {
    title: TXTWCL_Deposit_methods_help,
    body: <HelpSectionContent />
  };

  return (
    <Container
      page={DepositMethod}
      layoutClassName={classNames(container, {
        [gridLayout]: applyGridLayout
      })}
      pageClassName={classNames(innerPage, { [gridLayout]: applyGridLayout })}
      headerProps={{
        title: <Translation {...TXTWCL_Deposit} />,
        buttons: {
          back: !rmfPassed,
          onBackClick: goBack,
          allowMultiClick: skipSavedMethods
        },
        showExitPrompt: showExitPrompt()
      }}
      hideBonusSummary
      helpSection={helpSection}
      limitsSection={Site.UK === site}
      showRegulatoryInformation
      helpSupportResolver
    >
      {!isSwcSignupRmfEcoEransitionTwo &&
        !isFrance &&
        rmfPassed &&
        !Object.keys(paySystemDetails).length && (
          <Prompt
            title={{ translation: TXTWCL_Almost_there }}
            secondaryTitle={{ translation: TXTWCL_Next_upXX }}
            primaryButton={{
              translation: TXTWCL_Continue
            }}
            noCloseButton
            variant="celebration"
          >
            <Typography>
              <Translation {...TXTWCL_Make_your_first_deposit_and_cl_ELL} />
            </Typography>
          </Prompt>
        )}

      {rmfPassed && isSwcSignupRmfEcoEransitionTwo && !Object.keys(paySystemDetails).length && (
        <TransitionProgressPrompt
          title={{ translation: TXTCLI_Almost_done }}
          primaryButton={{ translation: TXTWCL_Continue_to_deposit }}
        >
          <Typography>
            <Translation {...TXTCLI_Once_you_depositX_youXll_be_go_ELL} />
          </Typography>
        </TransitionProgressPrompt>
      )}

      {showIneligiblePrompt && (
        <BonusIneligiblePrompt
          onClose={() => setShowIneligiblePrompt(false)}
          bonusIneligibleCallback={bonusIneligibleCallback}
        />
      )}

      {isEmbedded ? (
        <CSSTransition
          in={showSavedCards}
          timeout={duration}
          onExited={() => {
            setIsTransitioning(false);
          }}
          classNames={!showSavedCards ? rtl : ltr}
          unmountOnExit
        >
          <div className={classNames({ [transitionContainer]: isTransitioning })}>
            <div className={classNames({ [transitionInnerContainer]: isTransitioning })}>
              {savedMethodsScreen}
            </div>
          </div>
        </CSSTransition>
      ) : (
        showSavedCards && <div>{savedMethodsScreen}</div>
      )}
      {isEmbedded ? (
        <CSSTransition
          in={!showSavedCards}
          timeout={duration}
          classNames={!showSavedCards ? rtl : ltr}
          unmountOnExit
          onEntered={() => {
            setIsTransitioning(false);
          }}
        >
          <div className={classNames({ [transitionContainer]: isTransitioning })}>
            <div className={classNames({ [transitionInnerContainer]: isTransitioning })}>
              {regularMethodsScreen}
            </div>
          </div>
        </CSSTransition>
      ) : (
        !showSavedCards && <div>{regularMethodsScreen}</div>
      )}
    </Container>
  );
};

DepositMethod.displayName = 'DepositMethod';

export default DepositMethod;
